import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from './company.service';
import { UserService } from './user.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';
import { SocketService } from './socket.service';
import { RoleService } from './role.service';
import { debounceTime } from 'rxjs/operators';
import { OnboardingService } from './on-boarding.service';
import { UserJourneyService } from './user-journey.service';
/**
 * Service for authentication
 */
@Injectable({ providedIn: 'root' })
/**
 * Service for authentication
 * @class AuthService
 */
export class AuthService {
  /**
   * inject services
   * @type {StorageService}
   */
  private storageService = inject(StorageService);
  /**
   * inject services
   * @type {HttpClient}
   */
  private http = inject(HttpClient);
  /**
   * inject services
   * @type {NgbModal}
   */
  private modalService = inject(NgbModal);
  /**
   * inject services
   * @type {CompanyService}
   */
  private companyService = inject(CompanyService);
  /**
   * inject services
   * @type {UserService}
   */
  private userService = inject(UserService);
  /**
   * inject services
   * @type {NgZone}
   */
  private zone = inject(NgZone);
  /**
   * inject services
   * @type {Router}
   */
  private router = inject(Router);
  /**
   * inject services
   * @type {ToastrService}
   */
  private toastService = inject(ToastrService);
  /**
   * inject services
   * @type {TranslateService}
   */
  private translateService = inject(TranslateService);
  /**
   * inject services
   * @type {SocketService}
   */
  private socketService = inject(SocketService);
  /**
   * inject services
   * @type {RoleService}
   */
  private roleService = inject(RoleService);
  /**
   * show loading
   * @type {BehaviorSubject<boolean>}
   */
  private showLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  /**
   * base api url
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'manager/';
  /**
   * echo instance
   * @type {any}
   */
  private echoInstance = null;
  /**
   * force logout subscription
   * @type {Subscription}
   */
  private forceLogoutSub: Subscription = null;
  /**
   * flag to prevent multiple concurrent logout calls
   * @type {boolean}
   */
  private isLoggingOut = false;
  /**
   * inject services
   * @type {OnboardingService}
   */
  private onBoardingService = inject(OnboardingService);
  /**
   * inject services
   * @type {UserJourneyService}
   */
  private userJourneyService = inject(UserJourneyService);

  /**
   * Start listening to socket for current user events
   * Subscribe to force logout event and listen to events:
   * - CurrentUserDeletedEvent: log out user
   * - CurrentUserDesactivatedEvent: log out user
   */
  startSocket(): void {
    this.forceLogoutSub = this.userService.SubForceLogout().subscribe(() => {
      this.logout(true);
    });
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.user.${this.socketService.userId}`)
      .listen('.CurrentUserDeletedEvent', () => {
        this.logout(true);
      })
      .listen('.CurrentUserDesactivatedEvent', () => {
        this.logout(true);
      });
  }

  /**
   * Stop listening to socket for current user events
   * Leave the private channel that was subscribed in startSocket()
   */
  stopSocket(): void {
    if (this.forceLogoutSub) {
      this.forceLogoutSub.unsubscribe();
      this.forceLogoutSub = null;
    }
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.user.${this.socketService.userId}`);
    }
  }

  /**
   * Set the current loading status
   * @param status boolean
   * @description
   * This function set the loading status of the application
   * and notifies all subscribers of the change
   */
  SetCurrentLoadingStatus(status: boolean): void {
    this.showLoading$.next(status);
  }

  /**
   * Returns an observable for the current loading status.
   * @returns {Observable<boolean>} An observable emitting the loading status as a boolean.
   */
  SubCurrentLoadingStatus(): Observable<boolean> {
    return this.showLoading$;
  }

  /**
   * Verifies the credentials of the user
   * @param data
   * @description
   * This function sends a post request to the api with the data
   * and returns a promise of the response
   * @returns {Promise<any>}
   */

  async verifCredentials(data: any): Promise<any> {
    const res = await firstValueFrom(this.http.post(this.baseApiUrl + 'credentials', data, { observe: 'response' }));
    return { userRegion: res.headers.get('x-mycena-region'), suc: res.body };
  }

  async verifCredentialsAttento(data: any): Promise<any> {
    const res = await firstValueFrom(this.http.post(this.baseApiUrl + 'credentials/atento', data, { observe: 'response' }));
    return { userRegion: res.headers.get('x-mycena-region'), suc: res.body };
  }

  /**
   * Logins the user with the given credentials
   * @param data The object with the credentials of the user
   * @returns {Promise<any>} A promise of the response
   */
  login(data: any): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'login', data, { withCredentials: true }));
  }

  /**
   * Sends a forget password request to the server
   * @param email The email address of the user to send the password reset link to
   * @returns {Promise<any>} A promise of the response
   */
  forgetPassword(email: string): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'password/email', { email }));
  }

  /**
   * Checks the validity of a password reset token.
   * @param token The password reset token to verify.
   * @returns {Promise<any>} A promise of the server's response indicating the token's validity.
   */
  checkToken(token: string): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'password/reset/verif', { token }));
  }

  /**
   * Resets the password of a user using a password reset token.
   * @param data The object containing the password reset token and the new password.
   * @returns {Promise<any>} A promise of the server's response indicating the success of the password reset.
   */
  resetPassword(data: any): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'password/reset', data));
  }

  /**
   * Sends a request to reset the a2f token for the given email address
   * @param email The email address to reset the a2f token for
   * @returns {Promise<any>} A promise of the response
   */
  reseta2f(email: string): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'a2f/reset', { email }));
  }


  /**
   * Validates the reset of the a2f token.
   * @param data The data object containing necessary information for validation.
   * @returns {Promise<any>} A promise of the server's response indicating the success or failure of the validation.
   */
  validateReseta2f(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 'a2f/validReset', data));
  }

  /**
   * Logs out the current user by performing several cleanup tasks.
   * Dismisses all open modal dialogs and sends a logout request to the server.
   * Clears specific items from local storage such as selected columns.
   * Displays a warning toast message based on the logout reason, such as session expiration, IP restriction, or company subscription expiration.
   * Navigates the user to the authentication page and clears stored user and company information.
   * Stops socket connections to cease listening for user events.
   * 
   * @param sessionExpired - Indicates if the session expired.
   * @param ipRestricted - Indicates if the IP address is no longer authorized.
   * @param companyExpired - Indicates if the company subscription expired.
   */
  logout(sessionExpired = false, ipRestricted = false, companyExpired = false): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    this.modalService.dismissAll();
    firstValueFrom((this.isLoggedIn() ? this.http.get(this.baseApiUrl + 'logout', { withCredentials: true }) : of(null)))
      .then(() => {
        // Clear chatbot history
        this.storageService.clearChatbotHistory();

        for (const [key] of Object.entries(localStorage)) {
          if (key === 'userSelectedColumns' || key === 'preloadPasswordSelectedColumns'
            || key === 'preloadUrlSelectedColumns' || key === 'creditCardSelectedColumns'
            || key === 'applicationAccessSelectedColumns' || key === 'userAccessKitSelectedColumns'
            || key === 'filterAdSelectedColumns' || key === 'roleSelectedColumns'
            || key === 'grcSelectedColumns') {
            localStorage.removeItem(key);
          }
        }
      })
      .catch(() => { })
      .finally(() => {
        if (sessionExpired) {
          if (companyExpired) {
            this.toastService.warning(this.translateService.instant('globalToast.subscriptionExpired'));
          } else if (ipRestricted) {
            this.toastService.warning(this.translateService.instant('globalToast.ipNoMoreAuthorized'));
          } else {
            this.toastService.warning(this.translateService.instant('globalToast.sessionExpired'));
          }
        }
        this.socketService.disconnectSocket();
        this.userService.setCurrentUser(null);
        this.companyService.setCurrentCompany(null);
        this.storageService.clearSessionStorageData();
        this.stopSocket();
        this.userService.stopProgressNotification();
        this.userService.stopSocket();
        this.companyService.stopSocket();
        this.roleService.stopSocket();
        this.zone.run(() => {
          this.router.navigate(['auth']);
        });
        this.onBoardingService.destroyPopover();
        this.userJourneyService.setJourneySteps([]);
        this.isLoggingOut = false;
      });
  }

  /**
   * Determines if the user is logged in or not.
   * @returns {boolean} True if the user is logged in, false otherwise.
   */
  isLoggedIn(): boolean {
    if (this.storageService.getToken()) {
      return true;
    } else {
      return false;
    }
  }
}
